const WRAPPER_SELECTOR = '[data-component="product-bundle"]';
const ITEM_SELECTOR = '[data-bundle-item]';
const ITEM_TOGGLE_SELECTOR = '[data-bundle-item-toggle]';
const ITEM_VARIATION_SELECTOR = '[data-bundle-variation-select]';
const ITEM_REGULAR_PRICE_SELECTOR = '[data-bundle-item-regular-price]';
const ITEM_DISCOUNTED_PRICE_SELECTOR = '[data-bundle-item-discounted-price]';
const MAIN_PRICE_SELECTOR = '[data-bundle-main-price]';
const TOTAL_REGULAR_SELECTOR = '[data-bundle-total-regular]';
const TOTAL_DISCOUNTED_SELECTOR = '[data-bundle-total-discounted]';
const TOTAL_SAVINGS_SELECTOR = '[data-bundle-total-savings-value]';
const FEEDBACK_SELECTOR = '[data-bundle-feedback]';
const SUBMIT_SELECTOR = '[data-bundle-submit]';

const parseNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
};

const parseInteger = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : fallback;
};

const makePriceFormatter = (component) => {
    const symbol = component.dataset.currencySymbol || 'zł';
    const position = component.dataset.currencyPosition || 'right_space';
    const decimalSeparator = component.dataset.currencyDecimalSeparator || ',';
    const thousandSeparator = component.dataset.currencyThousandSeparator || ' ';
    const decimals = Math.max(0, parseInteger(component.dataset.currencyDecimals, 2));

    const numberFormatter = (rawValue) => {
        const value = Math.max(0, parseNumber(rawValue, 0));
        const fixed = value.toFixed(decimals);
        const [integerPart, decimalPart = ''] = fixed.split('.');
        const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
        return decimals > 0 ? `${groupedInteger}${decimalSeparator}${decimalPart}` : groupedInteger;
    };

    return (rawValue) => {
        const amount = numberFormatter(rawValue);
        switch (position) {
            case 'left':
                return `${symbol}${amount}`;
            case 'right':
                return `${amount}${symbol}`;
            case 'left_space':
                return `${symbol} ${amount}`;
            case 'right_space':
            default:
                return `${amount} ${symbol}`;
        }
    };
};

const setFeedback = (component, message, type = '') => {
    const feedbackElement = component.querySelector(FEEDBACK_SELECTOR);
    if (!feedbackElement) {
        return;
    }

    feedbackElement.textContent = message || '';
    feedbackElement.classList.remove('is-error', 'is-success');
    if (type === 'error') {
        feedbackElement.classList.add('is-error');
    }
    if (type === 'success') {
        feedbackElement.classList.add('is-success');
    }
};

const updateItemPriceView = (itemElement, priceLabel, discountedPriceLabel) => {
    const regularPriceElement = itemElement.querySelector(ITEM_REGULAR_PRICE_SELECTOR);
    const discountedPriceElement = itemElement.querySelector(ITEM_DISCOUNTED_PRICE_SELECTOR);

    if (regularPriceElement) {
        regularPriceElement.textContent = priceLabel;
    }

    if (discountedPriceElement) {
        discountedPriceElement.textContent = discountedPriceLabel;
    }
};

const readItemSelection = (itemElement, formatPrice) => {
    const productId = parseInteger(itemElement.dataset.productId, 0);
    const discountPercent = parseNumber(itemElement.dataset.discountPercent, 0);
    const isVariable = itemElement.dataset.isVariable === '1';
    const toggle = itemElement.querySelector(ITEM_TOGGLE_SELECTOR);
    const isSelected = !toggle || toggle.checked;

    let variationId = 0;
    let basePrice = parseNumber(itemElement.dataset.basePrice, 0);
    let discountedPrice = parseNumber(itemElement.dataset.discountedPrice, basePrice);
    let priceLabel = formatPrice(basePrice);
    let discountedPriceLabel = formatPrice(discountedPrice);

    if (isVariable) {
        const variationSelect = itemElement.querySelector(ITEM_VARIATION_SELECTOR);
        const selectedOption = variationSelect?.selectedOptions?.[0];

        if (selectedOption) {
            variationId = parseInteger(selectedOption.value, 0);
            basePrice = parseNumber(selectedOption.dataset.price, basePrice);
            discountedPrice = parseNumber(selectedOption.dataset.discountedPrice, discountedPrice);
            priceLabel = selectedOption.dataset.priceLabel || formatPrice(basePrice);
            discountedPriceLabel = selectedOption.dataset.discountedPriceLabel || formatPrice(discountedPrice);
        }
    }

    updateItemPriceView(itemElement, priceLabel, discountedPriceLabel);

    return {
        id: productId,
        isVariable,
        variationId,
        discountPercent,
        selected: isSelected,
        basePrice,
        discountedPrice,
    };
};

const readMainVariationFromForm = (variationForm) => {
    if (!(variationForm instanceof HTMLFormElement)) {
        return { variationId: 0, price: 0 };
    }

    const variationIdField = variationForm.querySelector('input.variation_id');
    const variationId = parseInteger(variationIdField?.value, 0);
    if (variationId <= 0) {
        return { variationId: 0, price: 0 };
    }

    const rawVariations = variationForm.getAttribute('data-product_variations') || '';
    if (!rawVariations) {
        return { variationId, price: 0 };
    }

    try {
        const parsedVariations = JSON.parse(rawVariations);
        if (!Array.isArray(parsedVariations)) {
            return { variationId, price: 0 };
        }

        const matched = parsedVariations.find((variation) => parseInteger(variation?.variation_id, 0) === variationId);
        if (!matched) {
            return { variationId, price: 0 };
        }

        return {
            variationId,
            price: parseNumber(matched.display_price, 0),
        };
    } catch (error) {
        return { variationId, price: 0 };
    }
};

const initializeBundleComponent = (component) => {
    const ajaxUrl = window.jsData?.ajaxUrl || '';
    const action = component.dataset.action || '';
    const nonce = component.dataset.nonce || '';
    const mainProductId = parseInteger(component.dataset.mainProductId, 0);
    const mainIsVariable = component.dataset.mainProductVariable === '1';
    const defaultMainPrice = parseNumber(component.dataset.mainBasePrice, 0);
    const mainPriceElement = component.querySelector(MAIN_PRICE_SELECTOR);
    const totalRegularElement = component.querySelector(TOTAL_REGULAR_SELECTOR);
    const totalDiscountedElement = component.querySelector(TOTAL_DISCOUNTED_SELECTOR);
    const totalSavingsElement = component.querySelector(TOTAL_SAVINGS_SELECTOR);
    const submitButton = component.querySelector(SUBMIT_SELECTOR);
    const variationForm = document.querySelector('form.variations_form');
    const formatPrice = makePriceFormatter(component);
    const errorMainVariation =
        component.dataset.errorMainVariation || 'Wybierz wariant produktu głównego, aby dodać zestaw.';
    const errorBundleVariation =
        component.dataset.errorBundleVariation || 'Wybierz wariant dla wszystkich zaznaczonych produktów zestawu.';
    const errorGeneric = component.dataset.errorGeneric || 'Nie udało się dodać zestawu. Spróbuj ponownie.';
    const successMessage = component.dataset.successMessage || 'Dodano zestaw do koszyka.';

    if (!submitButton || !action || !nonce || !mainProductId || !ajaxUrl) {
        return;
    }

    const state = {
        mainVariationId: 0,
        mainPrice: defaultMainPrice,
        isSubmitting: false,
    };

    const refreshSummary = () => {
        const itemElements = component.querySelectorAll(ITEM_SELECTOR);
        let totalRegular = state.mainPrice;
        let totalDiscounted = state.mainPrice;

        itemElements.forEach((itemElement) => {
            const selection = readItemSelection(itemElement, formatPrice);
            if (!selection.selected) {
                return;
            }

            totalRegular += selection.basePrice;
            totalDiscounted += selection.discountedPrice;
        });

        totalRegular = Math.max(0, totalRegular);
        totalDiscounted = Math.max(0, totalDiscounted);
        const savings = Math.max(0, totalRegular - totalDiscounted);

        if (mainPriceElement) {
            mainPriceElement.textContent = formatPrice(state.mainPrice);
        }
        if (totalRegularElement) {
            totalRegularElement.textContent = formatPrice(totalRegular);
        }
        if (totalDiscountedElement) {
            totalDiscountedElement.textContent = formatPrice(totalDiscounted);
        }
        if (totalSavingsElement) {
            totalSavingsElement.textContent = formatPrice(savings);
        }
    };

    const collectSelectedItems = () => {
        const selectedItems = [];
        const itemElements = component.querySelectorAll(ITEM_SELECTOR);

        for (const itemElement of itemElements) {
            const selection = readItemSelection(itemElement, formatPrice);
            if (!selection.selected) {
                continue;
            }

            if (selection.isVariable && selection.variationId <= 0) {
                return { selectedItems: [], hasVariationError: true };
            }

            selectedItems.push({
                id: selection.id,
                variation_id: selection.variationId > 0 ? selection.variationId : 0,
                discount_percent: selection.discountPercent,
            });
        }

        return { selectedItems, hasVariationError: false };
    };

    const syncMainVariationFromForm = () => {
        if (!mainIsVariable || !(variationForm instanceof HTMLFormElement)) {
            return;
        }

        const currentState = readMainVariationFromForm(variationForm);
        state.mainVariationId = currentState.variationId;

        if (currentState.price > 0) {
            state.mainPrice = currentState.price;
        } else if (currentState.variationId <= 0) {
            state.mainPrice = defaultMainPrice;
        }
    };

    if (mainIsVariable && window.jQuery && variationForm) {
        const $variationForm = window.jQuery(variationForm);
        $variationForm.on('found_variation.productBundle show_variation.productBundle', (event, variation) => {
            state.mainVariationId = parseInteger(variation?.variation_id, 0);
            state.mainPrice = parseNumber(variation?.display_price, defaultMainPrice);
            refreshSummary();
        });

        $variationForm.on('hide_variation.productBundle reset_data.productBundle', () => {
            state.mainVariationId = 0;
            state.mainPrice = defaultMainPrice;
            refreshSummary();
        });

        requestAnimationFrame(() => {
            syncMainVariationFromForm();
            refreshSummary();
        });
    }

    component.addEventListener('change', (event) => {
        const target = event.target;
        if (
            !(target instanceof Element) ||
            (!target.matches(ITEM_TOGGLE_SELECTOR) && !target.matches(ITEM_VARIATION_SELECTOR))
        ) {
            return;
        }

        setFeedback(component, '');
        refreshSummary();
    });

    submitButton.addEventListener('click', async () => {
        if (state.isSubmitting) {
            return;
        }

        syncMainVariationFromForm();
        if (mainIsVariable && state.mainVariationId <= 0) {
            setFeedback(component, errorMainVariation, 'error');
            return;
        }

        const { selectedItems, hasVariationError } = collectSelectedItems();
        if (hasVariationError) {
            setFeedback(component, errorBundleVariation, 'error');
            return;
        }

        state.isSubmitting = true;
        submitButton.disabled = true;
        submitButton.classList.add('is-loading');
        setFeedback(component, '');

        try {
            const body = new URLSearchParams();
            body.append('action', action);
            body.append('nonce', nonce);
            body.append('main_product_id', String(mainProductId));
            body.append('main_variation_id', String(state.mainVariationId > 0 ? state.mainVariationId : 0));
            body.append('items', JSON.stringify(selectedItems));

            const response = await fetch(ajaxUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                },
                body: body.toString(),
            });

            const result = await response.json();
            if (!response.ok || !result || result.success !== true) {
                const backendMessage = result?.data?.message;
                throw new Error(typeof backendMessage === 'string' && backendMessage ? backendMessage : errorGeneric);
            }

            setFeedback(component, result?.data?.message || successMessage, 'success');

            if (window.jQuery) {
                document.dispatchEvent(new CustomEvent('ovalia:open-cart'));
            }
        } catch (error) {
            const message = typeof error?.message === 'string' && error.message ? error.message : errorGeneric;
            setFeedback(component, message, 'error');
        } finally {
            state.isSubmitting = false;
            submitButton.disabled = false;
            submitButton.classList.remove('is-loading');
        }
    });

    refreshSummary();
};

document.addEventListener('DOMContentLoaded', () => {
    const components = document.querySelectorAll(WRAPPER_SELECTOR);
    components.forEach(initializeBundleComponent);
});
